import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { DatePickerModule } from 'primeng/datepicker';
import { StepperModule } from 'primeng/stepper';
import { RadioButtonModule } from 'primeng/radiobutton';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { AuthStoreService, ILoginSignal } from '../../services/auth-store.service';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ChallengeQuestion } from '../../interfaces/ILoginEmployee';
import { IChallengeQuestionsReq } from '../../interfaces/IChallengeQuestions';
import { SStorageService } from 'src/app/shared/services/sessionStorage.service';

// Define una interfaz para las respuestas
interface RespuestaPregunta {
  id: string; // ID de la pregunta (ej: "telefono")
  opcion: string; // Opción seleccionada (ej: "3135616167")
}

@Component({
  selector: 'app-login-cedula',
  imports: [StepperModule, CommonModule, DatePickerModule, RadioButtonModule, StackDirectives, FormsModule, ButtonModule],
  templateUrl: './login-cedula.component.html',
  styleUrl: './login-cedula.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginCedulaComponent {
  authStore = inject(AuthStoreService);
  authService = inject(AuthService);
  sstorageService = inject(SStorageService); // Usar SStorageService para acceder a los datos de sesión

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private loader: LoaderService,
    private cdr: ChangeDetectorRef // Añadir ChangeDetectorRef
  ) {
    //Valida que en el authStore exista el usuario
    if (!this.authStore.loginData().usuario) {
      this.router.navigate(['/login']);
    }
  }

  activeStep: number = 1; // Índice del stepper

  // Preguntas de reto
  preguntas: ChallengeQuestion[] = [];
  activePregunta = 0;
  // Cambia el tipo de respuestas
  respuestas: RespuestaPregunta[] = [];

  nextStep(step: number) {
    if (this.activePregunta == 0 && step === 2) {
      this.loginEmployee();
    } else {
      this.activeStep = step;
      this.cdr.detectChanges(); // Forzar la detección de cambios para actualizar la vista
    }
  }
  prevStep(step: number) {
    if (step === 1) {
      this.router.navigate(['/login']);
      return;
    }
    this.activeStep = step;
    this.cdr.detectChanges();
  }

  nextPregunta() {
    if (this.activePregunta < this.preguntas.length - 1) {
      this.activePregunta++;
      this.cdr.detectChanges();
    } else {
      // Si es la última pregunta, manejar el inicio de sesión
      this.handleLoginSuccess();
    }
  }

  prevPregunta() {
    if (this.activePregunta > 0) {
      this.activePregunta--;
      this.cdr.detectChanges();
    }
  }

  loginEmployee() {
    localStorage.clear();
    this.loader.show('Iniciando sesión...');
    const cedula = this.authStore.loginData().usuario;
    let fechaNacimiento: string | Date | undefined = this.authStore.loginData().fechaNacimiento;
    let fechaNacimientoStr = '';

    if (fechaNacimiento instanceof Date) {
      const day = String(fechaNacimiento.getDate()).padStart(2, '0');
      const month = String(fechaNacimiento.getMonth() + 1).padStart(2, '0');
      const year = fechaNacimiento.getFullYear();
      fechaNacimientoStr = `${day}/${month}/${year}`;
    } else if (typeof fechaNacimiento === 'string') {
      fechaNacimientoStr = fechaNacimiento;
    }

    this.authService.loginEmployee(cedula, fechaNacimientoStr).subscribe({
      next: (response) => {
        if (!response.success || !response.data) {
          this.loader.hide();
          this.toastr.error(response.message || 'Error al iniciar sesión', 'Error');
          return;
        }
        this.loader.show('Cargando preguntas de seguridad...');

        // Guardar el token
        this.sstorageService.setItem('token', response.data.token);
        this.sstorageService.setItem('mod', '1'); // Mientras se inicia sesion, se asume que es el módulo 1
        // Login de empleado: no es sesión administrativa (Azure). Si quedó 'msal' de una sesión
        // anterior que expiró sin logout, el front lo tomaría como administrativo (chat, mood).
        this.sstorageService.removeItem('msal');

        // Obtener las preguntas directamente de la respuesta
        if (response.data.challengeQuestions && response.data.challengeQuestions.length > 0) {
          // // console.log('Preguntas recibidas:', response.data.challengeQuestions);

          // Asignar las preguntas de seguridad
          this.loader.show('Cargando preguntas de seguridad...');
          this.preguntas = response.data.challengeQuestions;

          // Inicializar el array de respuestas con objetos vacíos
          this.respuestas = this.preguntas.map((pregunta) => ({
            id: pregunta.id,
            opcion: ''
          }));

          // Reiniciar el índice de pregunta activa
          this.activePregunta = 0;

          // Avanzar al paso 2
          this.activeStep = 2;

          // Forzar la detección de cambios para actualizar la vista
          this.cdr.detectChanges();

          this.loader.hide();
        } else {
          this.loader.hide();
          this.toastr.warning('No se encontraron preguntas de seguridad', 'Advertencia');
          // Si no hay preguntas, avanzar directamente
          this.validateAnswers();
        }

        // console.log('Paso 1: Login exitoso', this.activeStep);
      },
      error: (error) => {
        this.loader.hide();
        if (error.status === 401) {
          this.toastr.error('Credenciales inválidas', 'Error');
        } else {
          this.toastr.error('Error en el login', 'Error');
        }
        console.error('Error en el login:', error);
      }
    });
  }

  validateAnswers() {
    this.loader.show('Verificando respuestas...');
    // console.log('Respuestas a validar:', this.respuestas);

    // Ya no necesitas crear un objeto separado, tus respuestas ya tienen el formato correcto
    const respuestasObj = this.respuestas;

    // Verificar que todas las respuestas tengan una opción seleccionada
    const todasRespondidasCorrectamente = respuestasObj.every((r) => r.opcion && r.opcion !== '');

    if (!todasRespondidasCorrectamente) {
      this.loader.hide();
      this.toastr.error('Debes responder todas las preguntas', 'Error');
      return;
    }

    // Obtener el token del sessionStorage
    const token = this.sstorageService.getItem('token');

    // Verificar que el token exista
    if (!token) {
      this.loader.hide();
      this.toastr.error('No se encontró el token de autenticación', 'Error');
      return;
    }

    let payload: IChallengeQuestionsReq = {} as IChallengeQuestionsReq;

    payload = {
      telefono: respuestasObj.find((r) => r.id === 'telefono')?.opcion || null,
      email: respuestasObj.find((r) => r.id === 'email')?.opcion || null,
      ciudadExpedicion: respuestasObj.find((r) => r.id === 'ciudadExpedicion')?.opcion || null,
      ciudadNacimiento: respuestasObj.find((r) => r.id === 'ciudadNacimiento')?.opcion || null,
      direccion: respuestasObj.find((r) => r.id === 'direccion')?.opcion || null
    };

    if (this.authService.challengeQuestions) {
      this.authService.challengeQuestions?.(token, payload)?.subscribe({
        next: (response) => {
          this.loader.hide();
          if (response.success) {
            this.handleLoginSuccess();
          } else {
            this.toastr.error(response.message || 'Error al validar respuestas', 'Error');
          }
        },
        error: (error) => {
          this.authStore.loginData().usuario = '';
          this.authStore.loginData().password = '';
          this.authStore.loginData().fechaNacimiento = undefined;
          this.loader.hide();
          this.toastr.error(error.error.detalle, 'Error');
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 3000);
          console.error('Error al validar respuestas:', error);
          this.authStore.loginData.set({} as ILoginSignal);
        }
      });
    } else {
      this.loader.hide();
      this.toastr.error('No se encontró el método de validación de preguntas', 'Error');
    }
  }

  handleLoginSuccess(): void {
    this.loader.show('Cargando información del usuario...'); // Muestra el loader mientras se carga la información del usuario
    const token = this.sstorageService.getItem('token');

    this.authService.getInfoUser(token)?.subscribe({
      next: (userInfo) => {
        this.sstorageService.setItem('userInfo', JSON.stringify(userInfo.data));
        this.router.navigate(['app/mi-portal/inicio']);
      },
      error: (error) => {
        this.loader.hide(); // Oculta el loader en caso de error al obtener la información del usuario
        console.error('Error al obtener información del usuario:', error);
        this.toastr.error('Error al obtener información del usuario', 'Error');
      }
    });
  }
}
